// Wave 20.44 — shared fetch for GET /sessions/me/positions/analytics.
//
// AeroPositionsPanel (Feed sidebar) and WakeScreen both need the same
// per-position rollup. Same JWT + 402 (Pro-only) handling; each caller
// renders it differently.
//
// Returned states are mutually exclusive:
//   loading = true                 → still fetching (data null, error null)
//   error = 'paywall'              → user is on the free tier
//   error = 'no_token' | 'other'   → sign in or transient failure
//   data                           → payload — check `positions.length` for empty state

import { useCallback, useEffect, useState } from 'react';

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

export interface PositionStat {
  class: string;
  // Wave 20.47f — rider-authored name for this class (nullable).
  alias?: string | null;
  bestCda: number;
  meanCda: number;
  worstCda: number;
  totalHoldSec: number;
  sessionCount: number;
  firstSeen: string;
  lastSeen: string;
  bestSessionId: string;
  // Wave 20.47f — recent per-session bests for a mini sparkline.
  // Wave 20.49n — each entry now carries `cdaMean` so the wake
  // visualization can render one line per measurement with per-
  // measurement waviness (variability ≈ cdaMean - bestCda).
  // Wave 20.49q — each entry carries the per-session calibration
  // snapshot so PositionDetail can render a per-row thumbnail of
  // the framing the rider used that day.
  // Wave 20.49s — `bestCda` / `cdaMean` are NORMALIZED to the
  // rider's running-median A_cal so cross-session numbers are
  // rig-neutral. `bestCdaRaw` preserves the untouched value.
  // `cdaNormalized` is false when the correction was skipped
  // (session pre-dates the snapshot, pose was too far from the
  // rider's historical centroid to trust, or fewer than 3 valid
  // A_cal samples exist yet). UI shows a small "raw" badge when
  // false so riders know that row is unnormalized.
  recentBests?: Array<{
    sessionId: string;
    startedAt: string;
    bestCda: number;
    cdaMean?: number;
    bestCdaRaw?: number;
    cdaNormalized?: boolean;
    calibrationSnapshot?: {
      shoulderMid: { x: number; y: number };
      hipMid: { x: number; y: number };
      silhouettePngB64: string | null;
      aCalM2?: number | null;
      capturedAt: string;
    } | null;
  }>;
}

export interface PositionsAnalyticsPayload {
  positions: PositionStat[];
  totalSessions: number;
  lifetimeBest: { class: string | null; cda: number | null };
}

export type PositionsError = 'paywall' | 'no_token' | 'other';

export interface PositionsAnalyticsState {
  data: PositionsAnalyticsPayload | null;
  loading: boolean;
  error: PositionsError | null;
  errorMessage: string | null;
  reload: () => void;
}

export function usePositionsAnalytics(): PositionsAnalyticsState {
  const [data, setData] = useState<PositionsAnalyticsPayload | null>(null);
  const [error, setError] = useState<PositionsError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    setErrorMessage(null);
    setLoading(true);
    const jwt = localStorage.getItem(TOKEN_KEY);
    if (!jwt) {
      setError('no_token');
      setErrorMessage('Sign in to see your positions.');
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API}/sessions/me/positions/analytics`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (res.status === 402) {
        setError('paywall');
        setErrorMessage('Aero positions require a Pro subscription.');
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setError('other');
        setErrorMessage(`Failed to load (${res.status})`);
        setLoading(false);
        return;
      }
      setData((await res.json()) as PositionsAnalyticsPayload);
      setLoading(false);
    } catch (e: any) {
      setError('other');
      setErrorMessage(e?.message || 'Unable to load positions');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, errorMessage, reload: load };
}
